/**
 * Community Demo Data Configuration
 *
 * This file contains all demo/fake data for community features:
 * - The Collective (grows & presets)
 * - Forum (posts, topics, replies)
 *
 * IMPORTANT: To switch demo mode from fake data to real public data,
 * simply change USE_FAKE_DATA_IN_DEMO to false. This will make demo
 * mode fetch real public data from the database (read-only for demo users).
 */

// ============================================================================
// DEMO DATA CONFIGURATION - Change this single variable to switch data source
// ============================================================================
export const USE_FAKE_DATA_IN_DEMO = true;

// ============================================================================
// THE COLLECTIVE - DEMO DATA
// ============================================================================

export const DEMO_FEATURED_GROWS = [
  {
    id: 'grow-1',
    name: 'Genovese Basil Indoor',
    author: 'HydroNerd',
    authorId: 'demo-user-1',
    rating: 4.9,
    reviews: 127,
    plantType: 'herbs',
    medium: 'hydroponic',
    tags: ['indoor', 'LED', 'DWC'],
    description: 'A complete guide to growing Genovese basil indoors with DWC hydroponics.',
    createdAt: '2024-11-15T10:30:00Z',
    imageUrl: null,
  },
  {
    id: 'grow-2',
    name: 'Cherry Tomato Heaven',
    author: 'TomatoKing',
    authorId: 'demo-user-2',
    rating: 4.7,
    reviews: 89,
    plantType: 'tomato',
    medium: 'soil',
    tags: ['organic', 'indoor', 'beginner'],
    description: 'Perfect cherry tomatoes year-round with this beginner-friendly soil setup.',
    createdAt: '2024-10-22T14:15:00Z',
    imageUrl: null,
  },
  {
    id: 'grow-3',
    name: 'Hydro Lettuce Farm',
    author: 'LettuceGuru',
    authorId: 'demo-user-3',
    rating: 4.8,
    reviews: 156,
    plantType: 'lettuce',
    medium: 'hydroponic',
    tags: ['NFT', 'commercial', 'LED'],
    description: 'Commercial-scale NFT lettuce production with optimal sensor configurations.',
    createdAt: '2024-09-08T09:45:00Z',
    imageUrl: null,
  },
  {
    id: 'grow-4',
    name: 'Spicy Pepper Paradise',
    author: 'PepperHead',
    authorId: 'demo-user-4',
    rating: 4.6,
    reviews: 72,
    plantType: 'pepper',
    medium: 'coco',
    tags: ['hot peppers', 'greenhouse'],
    description: 'Growing the hottest peppers in coco coir with precise nutrient management.',
    createdAt: '2024-12-01T16:20:00Z',
    imageUrl: null,
  },
  {
    id: 'grow-5',
    name: 'Basil Bonanza',
    author: 'HerbMaster',
    authorId: 'demo-user-5',
    rating: 4.5,
    reviews: 43,
    plantType: 'herbs',
    medium: 'hydroponic',
    tags: ['kratky', 'windowsill', 'easy'],
    description: 'Simple Kratky method for endless fresh basil on your windowsill.',
    createdAt: '2024-11-28T11:00:00Z',
    imageUrl: null,
  },
  {
    id: 'grow-6',
    name: 'Strawberry Dreams',
    author: 'BerryQueen',
    authorId: 'demo-user-6',
    rating: 4.8,
    reviews: 98,
    plantType: 'strawberry',
    medium: 'hydroponic',
    tags: ['vertical', 'LED', 'sweet'],
    description: 'Vertical strawberry tower setup for maximum yield in minimal space.',
    createdAt: '2024-10-15T08:30:00Z',
    imageUrl: null,
  },
];

export const DEMO_FEATURED_PRESETS = [
  {
    id: 'preset-1',
    name: 'Herb Veg Stage',
    author: 'HydroNerd',
    authorId: 'demo-user-1',
    downloads: 1243,
    plantType: 'herbs',
    stage: 'vegetative',
    description: 'Optimized sensor ranges for vegetative herb growth.',
    sensors: {
      ph: { min: 5.8, max: 6.2 },
      ec: { min: 1.2, max: 1.8 },
      airTemp: { min: 72, max: 82 },
      humidity: { min: 50, max: 70 },
    },
  },
  {
    id: 'preset-2',
    name: 'Tomato Flowering',
    author: 'TomatoKing',
    authorId: 'demo-user-2',
    downloads: 892,
    plantType: 'tomato',
    stage: 'flowering',
    description: 'Boost tomato flower production with these proven settings.',
    sensors: {
      ph: { min: 6.0, max: 6.5 },
      ec: { min: 2.0, max: 2.5 },
      airTemp: { min: 70, max: 80 },
      humidity: { min: 40, max: 60 },
    },
  },
  {
    id: 'preset-3',
    name: 'Universal Seedling',
    author: 'SeedStarter',
    authorId: 'demo-user-7',
    downloads: 2156,
    plantType: 'all',
    stage: 'seedling',
    description: 'Works for almost any seedling - gentle settings for delicate starts.',
    sensors: {
      ph: { min: 5.8, max: 6.5 },
      ec: { min: 0.4, max: 0.8 },
      airTemp: { min: 70, max: 78 },
      humidity: { min: 60, max: 80 },
    },
  },
  {
    id: 'preset-4',
    name: 'Pepper Heat Stress',
    author: 'PepperHead',
    authorId: 'demo-user-4',
    downloads: 567,
    plantType: 'pepper',
    stage: 'flowering',
    description: 'Controlled heat stress to maximize capsaicin production.',
    sensors: {
      ph: { min: 5.8, max: 6.2 },
      ec: { min: 1.8, max: 2.2 },
      airTemp: { min: 78, max: 88 },
      humidity: { min: 40, max: 55 },
    },
  },
  {
    id: 'preset-5',
    name: 'Lettuce Fast Track',
    author: 'LettuceGuru',
    authorId: 'demo-user-3',
    downloads: 1089,
    plantType: 'lettuce',
    stage: 'vegetative',
    description: 'Speed up lettuce growth with these optimized parameters.',
    sensors: {
      ph: { min: 5.5, max: 6.0 },
      ec: { min: 0.8, max: 1.2 },
      airTemp: { min: 65, max: 75 },
      humidity: { min: 50, max: 70 },
    },
  },
];

// ============================================================================
// FORUM - DEMO DATA
// ============================================================================

export const FORUM_TOPICS = [
  { id: 'topic-general', name: 'General Discussion', icon: 'chat', color: 'primary', description: 'General growing talk and community chat' },
  { id: 'topic-troubleshooting', name: 'Troubleshooting', icon: 'warning', color: 'error', description: 'Get help diagnosing and fixing plant issues' },
  { id: 'topic-nutrients', name: 'Nutrients & pH', icon: 'science', color: 'success', description: 'Discuss nutrient schedules, pH management, and feeding' },
  { id: 'topic-lighting', name: 'Lighting', icon: 'lightbulb', color: 'warning', description: 'LED, HPS, CMH - all lighting discussion' },
  { id: 'topic-hydroponics', name: 'Hydroponics', icon: 'water', color: 'info', description: 'DWC, NFT, Ebb & Flow, and all hydro methods' },
  { id: 'topic-sensors', name: 'Sensors & Automation', icon: 'sensors', color: 'secondary', description: 'Hardware, sensors, and automation setups' },
  { id: 'topic-harvest', name: 'Harvest & Post-Harvest', icon: 'agriculture', color: 'success', description: 'Harvesting techniques, storage, and post-harvest handling' },
  { id: 'topic-showcase', name: 'Grow Showcase', icon: 'photo', color: 'primary', description: 'Show off your grows and setups' },
];

export const DEMO_FORUM_POSTS = [
  {
    id: 'post-1',
    title: 'pH keeps dropping overnight - any ideas?',
    content: `I'm running a DWC setup with a 5-gallon bucket. Every morning I check my pH and it's dropped from 6.0 to around 5.2. I've tried:\n\n- Adding more pH up\n- Changing the water completely\n- Checking for light leaks\n\nMy plants are in week 3 of veg and otherwise look healthy. EC is stable at 1.4. Anyone else experience this? What could be causing such a big swing overnight?\n\nRunning:\n- 300W LED\n- Air pump with 2 stones\n- General Hydroponics Flora series`,
    author: 'HydroNewbie',
    authorId: 'demo-user-8',
    topicId: 'topic-troubleshooting',
    createdAt: '2024-12-27T14:30:00Z',
    updatedAt: '2024-12-27T14:30:00Z',
    views: 234,
    likes: 12,
    replyCount: 8,
    isPinned: false,
    isSolved: true,
    tags: ['DWC', 'pH', 'troubleshooting'],
  },
  {
    id: 'post-2',
    title: 'Best budget pH/EC meter combo for beginners?',
    content: `Hey everyone! Just getting started with hydroponics and need recommendations for a reliable pH and EC meter. Budget is around $50-100 total.\n\nI've seen the Apera and BlueLab mentioned a lot, but they're pricey. Are the cheaper Amazon options worth it, or should I save up for quality?\n\nWill be using it for a small DWC lettuce setup to start.`,
    author: 'GreenThumbStart',
    authorId: 'demo-user-9',
    topicId: 'topic-sensors',
    createdAt: '2024-12-26T09:15:00Z',
    updatedAt: '2024-12-26T18:45:00Z',
    views: 456,
    likes: 23,
    replyCount: 15,
    isPinned: false,
    isSolved: false,
    tags: ['pH meter', 'EC meter', 'beginner', 'budget'],
  },
  {
    id: 'post-3',
    title: '[GUIDE] Complete VPD Chart and Calculator Explained',
    content: `# Understanding VPD for Optimal Plant Growth\n\nVapor Pressure Deficit (VPD) is one of the most important metrics for controlling your grow environment. Here's everything you need to know:\n\n## What is VPD?\n\nVPD measures the difference between the amount of moisture in the air and the maximum moisture the air can hold at saturation. It's measured in kPa (kilopascals).\n\n## Optimal VPD Ranges:\n\n- **Seedlings/Clones**: 0.4 - 0.8 kPa\n- **Vegetative**: 0.8 - 1.2 kPa\n- **Early Flower**: 1.0 - 1.4 kPa\n- **Late Flower**: 1.2 - 1.6 kPa\n\n## How to Calculate:\n\nVPD = SVP(leaf) - AVP(air)\n\nWhere:\n- SVP = Saturation Vapor Pressure at leaf temperature\n- AVP = Actual Vapor Pressure of the air\n\nPro tip: Leaf temperature is typically 2-5°F cooler than air temperature under LED lights.\n\nLet me know if you have questions!`,
    author: 'HydroNerd',
    authorId: 'demo-user-1',
    topicId: 'topic-general',
    createdAt: '2024-12-20T10:00:00Z',
    updatedAt: '2024-12-20T10:00:00Z',
    views: 1892,
    likes: 156,
    replyCount: 34,
    isPinned: true,
    isSolved: false,
    tags: ['VPD', 'guide', 'humidity', 'temperature'],
  },
  {
    id: 'post-4',
    title: 'Yellow leaves during fruiting - nitrogen deficiency or natural fade?',
    content: `My tomato plants are in week 7 of fruiting and the lower leaves are turning yellow and falling off. Is this normal end-of-life fade or should I be worried?\n\nI've been following the same nutrient schedule since week 4:\n- 5ml/gal Micro\n- 10ml/gal Bloom\n- 2ml/gal CalMag\n\npH: 6.2\nEC: 1.8\n\nThe fruit clusters look great and are still growing. Just concerned about the yellowing spreading too fast.`,
    author: 'FirstTimeFruiter',
    authorId: 'demo-user-10',
    topicId: 'topic-nutrients',
    createdAt: '2024-12-25T16:45:00Z',
    updatedAt: '2024-12-25T16:45:00Z',
    views: 178,
    likes: 8,
    replyCount: 11,
    isPinned: false,
    isSolved: true,
    tags: ['nitrogen', 'deficiency', 'fruiting', 'yellowing'],
  },
  {
    id: 'post-5',
    title: 'Spider Farmer SF-2000 vs Mars Hydro TSW-2000 for 3x3 tent?',
    content: `Looking to upgrade from my blurple lights to a quality LED for my 3x3 tent. These two seem to be the most recommended in my price range ($200-300).\n\nAnyone have experience with both? Main concerns:\n- Light uniformity\n- Heat output\n- Actual power draw vs advertised\n- Longevity\n\nGrowing mostly peppers and tomatoes if that matters.`,
    author: 'LightHunter',
    authorId: 'demo-user-11',
    topicId: 'topic-lighting',
    createdAt: '2024-12-24T11:20:00Z',
    updatedAt: '2024-12-24T11:20:00Z',
    views: 567,
    likes: 31,
    replyCount: 28,
    isPinned: false,
    isSolved: false,
    tags: ['LED', 'Spider Farmer', 'Mars Hydro', 'comparison'],
  },
  {
    id: 'post-6',
    title: 'First hydroponic harvest - 2lb from 4 plants!',
    content: `Just finished my first ever hydroponic grow and couldn't be happier! Got just over 2 pounds dried from 4 tomato plants in a 4x4 tent.\n\nSetup:\n- RDWC with 4x 5-gallon buckets\n- HLG 550 V2 light\n- General Hydroponics Flora series\n- ArcticXtracts for monitoring (game changer!)\n\nThe ability to see all my sensor data in one place and get alerts when things went out of range saved my grow multiple times. pH swings were caught early before they became problems.\n\nHappy to answer any questions about my setup!`,
    author: 'TomatoKing',
    authorId: 'demo-user-2',
    topicId: 'topic-showcase',
    createdAt: '2024-12-23T20:00:00Z',
    updatedAt: '2024-12-23T20:00:00Z',
    views: 892,
    likes: 89,
    replyCount: 42,
    isPinned: false,
    isSolved: false,
    tags: ['showcase', 'RDWC', 'tomatoes', 'harvest'],
  },
  {
    id: 'post-7',
    title: 'Automating nutrient dosing - DIY or commercial solution?',
    content: `I'm looking to automate my nutrient dosing for a small commercial lettuce operation (100 plant sites). Currently spending 2+ hours a day manually adjusting.\n\nOptions I'm considering:\n\n1. **DIY Arduino/ESP32 setup** - Cheaper but requires coding knowledge\n2. **Bluelab pH Controller** - Proven but expensive (~$400)\n3. **Full commercial dosing system** - Way over budget\n\nAnyone built a reliable DIY system? Or is the Bluelab worth the investment for peace of mind?\n\nMy main requirements:\n- Automatic pH adjustment (up and down)\n- EC monitoring with alerts\n- Data logging\n- Remote monitoring`,
    author: 'CommercialGreens',
    authorId: 'demo-user-12',
    topicId: 'topic-hydroponics',
    createdAt: '2024-12-22T13:30:00Z',
    updatedAt: '2024-12-22T13:30:00Z',
    views: 723,
    likes: 45,
    replyCount: 37,
    isPinned: false,
    isSolved: false,
    tags: ['automation', 'dosing', 'commercial', 'DIY'],
  },
  {
    id: 'post-8',
    title: 'When to harvest peppers for maximum heat?',
    content: `Growing Carolina Reapers and want to maximize the capsaicin content. I've heard conflicting advice:\n\n1. Harvest when fully red and slightly wrinkled\n2. Harvest when starting to turn color for best flavor\n3. Stress the plant before harvest\n\nWhat's the science behind pepper heat development? Also curious if drying method affects final heat level.\n\nAny hot pepper growers have experience with this?`,
    author: 'PepperHead',
    authorId: 'demo-user-4',
    topicId: 'topic-harvest',
    createdAt: '2024-12-21T17:15:00Z',
    updatedAt: '2024-12-21T17:15:00Z',
    views: 445,
    likes: 38,
    replyCount: 22,
    isPinned: false,
    isSolved: true,
    tags: ['peppers', 'harvest', 'capsaicin', 'Carolina Reaper'],
  },
];

export const DEMO_FORUM_REPLIES = {
  'post-1': [
    {
      id: 'reply-1-1',
      postId: 'post-1',
      content: `This is super common with DWC! The pH drop is caused by your plants actively taking up nutrients - specifically, they release hydrogen ions as they absorb cations like calcium and potassium.\n\nA few things to try:\n1. Use a larger reservoir (bigger buffer)\n2. Top off with pH-adjusted water rather than adding more pH up\n3. Check your starting water - if it has low alkalinity it won't buffer well\n\nWhat's your source water? Tap, RO, or distilled?`,
      author: 'HydroNerd',
      authorId: 'demo-user-1',
      createdAt: '2024-12-27T15:45:00Z',
      likes: 8,
      isAcceptedAnswer: true,
    },
    {
      id: 'reply-1-2',
      postId: 'post-1',
      content: `^ This is the correct answer. I had the same issue and switching to a 10-gallon reservoir instead of 5-gallon made the pH much more stable. The larger volume gives you more buffer capacity.`,
      author: 'LettuceGuru',
      authorId: 'demo-user-3',
      createdAt: '2024-12-27T16:30:00Z',
      likes: 5,
      isAcceptedAnswer: false,
    },
    {
      id: 'reply-1-3',
      postId: 'post-1',
      content: `Also check if your air stones are creating CO2 off-gassing. Sometimes aggressive aeration can cause pH swings. Not usually this dramatic though.`,
      author: 'HydroHelper',
      authorId: 'demo-user-13',
      createdAt: '2024-12-27T18:00:00Z',
      likes: 3,
      isAcceptedAnswer: false,
    },
  ],
  'post-2': [
    {
      id: 'reply-2-1',
      postId: 'post-2',
      content: `For that budget, I'd recommend the **Apera PH20** for pH (~$50) and a separate EC meter. The combo units in that price range tend to be less reliable.\n\nThe Apera has served me well for 2 years with proper calibration and storage. Just remember to:\n- Calibrate monthly\n- Store in storage solution (not distilled water!)\n- Rinse between readings`,
      author: 'SensorNerd',
      authorId: 'demo-user-14',
      createdAt: '2024-12-26T10:00:00Z',
      likes: 12,
      isAcceptedAnswer: false,
    },
    {
      id: 'reply-2-2',
      postId: 'post-2',
      content: `Skip the cheap Amazon ones. I went through 3 of them in a year before finally getting an Apera. Wish I had just spent the money upfront.\n\nThe BlueLab is amazing but probably overkill for a small lettuce setup. Apera is the sweet spot for hobbyists.`,
      author: 'TomatoKing',
      authorId: 'demo-user-2',
      createdAt: '2024-12-26T11:30:00Z',
      likes: 15,
      isAcceptedAnswer: false,
    },
  ],
  'post-4': [
    {
      id: 'reply-4-1',
      postId: 'post-4',
      content: `Week 7 is when you'd expect to see the natural fade starting. The plant is redirecting mobile nutrients (like nitrogen) from the lower leaves to the fruit.\n\nIf it's only the lower leaves and the fade is gradual, that's totally normal and actually a good sign - your plant is using its reserves efficiently.\n\nIf the yellowing is rapid or affecting upper leaves, then you might have an issue.`,
      author: 'HydroNerd',
      authorId: 'demo-user-1',
      createdAt: '2024-12-25T17:30:00Z',
      likes: 6,
      isAcceptedAnswer: true,
    },
  ],
};

// Demo users for author lookups
export const DEMO_USERS = {
  'demo-user-1': { id: 'demo-user-1', username: 'HydroNerd', avatar: null, reputation: 1523, joinedAt: '2023-01-15' },
  'demo-user-2': { id: 'demo-user-2', username: 'TomatoKing', avatar: null, reputation: 892, joinedAt: '2023-03-22' },
  'demo-user-3': { id: 'demo-user-3', username: 'LettuceGuru', avatar: null, reputation: 1156, joinedAt: '2023-02-08' },
  'demo-user-4': { id: 'demo-user-4', username: 'PepperHead', avatar: null, reputation: 678, joinedAt: '2023-05-14' },
  'demo-user-5': { id: 'demo-user-5', username: 'HerbMaster', avatar: null, reputation: 445, joinedAt: '2023-07-30' },
  'demo-user-6': { id: 'demo-user-6', username: 'BerryQueen', avatar: null, reputation: 534, joinedAt: '2023-04-11' },
  'demo-user-7': { id: 'demo-user-7', username: 'SeedStarter', avatar: null, reputation: 2034, joinedAt: '2022-11-05' },
  'demo-user-8': { id: 'demo-user-8', username: 'HydroNewbie', avatar: null, reputation: 45, joinedAt: '2024-10-01' },
  'demo-user-9': { id: 'demo-user-9', username: 'GreenThumbStart', avatar: null, reputation: 23, joinedAt: '2024-11-15' },
  'demo-user-10': { id: 'demo-user-10', username: 'FirstTimeFruiter', avatar: null, reputation: 67, joinedAt: '2024-08-20' },
  'demo-user-11': { id: 'demo-user-11', username: 'LightHunter', avatar: null, reputation: 156, joinedAt: '2024-06-12' },
  'demo-user-12': { id: 'demo-user-12', username: 'CommercialGreens', avatar: null, reputation: 289, joinedAt: '2024-02-28' },
  'demo-user-13': { id: 'demo-user-13', username: 'HydroHelper', avatar: null, reputation: 567, joinedAt: '2023-09-18' },
  'demo-user-14': { id: 'demo-user-14', username: 'SensorNerd', avatar: null, reputation: 823, joinedAt: '2023-06-05' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get collective grows data based on demo mode setting
 * @param {boolean} isDemo - Whether in demo mode
 * @param {Function} fetchRealData - Function to fetch real data (used when USE_FAKE_DATA_IN_DEMO is false)
 */
export const getCollectiveGrows = async (isDemo, fetchRealData = null) => {
  if (isDemo && USE_FAKE_DATA_IN_DEMO) {
    return DEMO_FEATURED_GROWS;
  }
  // When not using fake data or in live mode, fetch from database
  if (fetchRealData) {
    return await fetchRealData();
  }
  return DEMO_FEATURED_GROWS; // Fallback
};

/**
 * Get collective presets data based on demo mode setting
 */
export const getCollectivePresets = async (isDemo, fetchRealData = null) => {
  if (isDemo && USE_FAKE_DATA_IN_DEMO) {
    return DEMO_FEATURED_PRESETS;
  }
  if (fetchRealData) {
    return await fetchRealData();
  }
  return DEMO_FEATURED_PRESETS;
};

/**
 * Get forum posts data based on demo mode setting
 */
export const getForumPosts = async (isDemo, fetchRealData = null) => {
  if (isDemo && USE_FAKE_DATA_IN_DEMO) {
    return DEMO_FORUM_POSTS;
  }
  if (fetchRealData) {
    return await fetchRealData();
  }
  return DEMO_FORUM_POSTS;
};

/**
 * Get forum replies for a specific post
 */
export const getForumReplies = async (postId, isDemo, fetchRealData = null) => {
  if (isDemo && USE_FAKE_DATA_IN_DEMO) {
    return DEMO_FORUM_REPLIES[postId] || [];
  }
  if (fetchRealData) {
    return await fetchRealData(postId);
  }
  return DEMO_FORUM_REPLIES[postId] || [];
};

/**
 * Get forum topics
 */
export const getForumTopics = () => {
  return FORUM_TOPICS;
};

/**
 * Check if user can post/reply (not in demo mode)
 */
export const canUserPost = (isDemo, isAuthenticated) => {
  return !isDemo && isAuthenticated;
};
